import { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Save, X, GripVertical, MessageSquare, Send, Loader2, Paperclip, FileText, Image } from 'lucide-react';
import { techniqueApi, adminApi, uploadApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

const ICONS = [
  { value: 'brain', label: 'Brain' },
  { value: 'castle', label: 'Castle' },
  { value: 'hash', label: 'Hash' },
  { value: 'calculator', label: 'Calculator' },
  { value: 'link', label: 'Link' },
  { value: 'clock', label: 'Clock' },
  { value: 'book', label: 'Book' },
  { value: 'lightbulb', label: 'Lightbulb' },
  { value: 'puzzle', label: 'Puzzle' },
  { value: 'target', label: 'Target' },
  { value: 'star', label: 'Star' },
  { value: 'zap', label: 'Zap' }
];

function ManageTechniques() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const [techniques, setTechniques] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTechnique, setEditingTechnique] = useState(null);
  const [testTechnique, setTestTechnique] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'brain',
    system_prompt_addition: '',
    sort_order: 0,
    is_active: true,
    levels: ['L1', 'L2']
  });

  useEffect(() => {
    loadTechniques();
  }, []);

  const loadTechniques = async () => {
    try {
      const response = await techniqueApi.getAllTechniques();
      setTechniques(response.data);
    } catch (error) {
      toast.error('Failed to load techniques');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTechnique(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: 'brain',
      system_prompt_addition: '',
      sort_order: techniques.length,
      is_active: true,
      levels: ['L1', 'L2']
    });
    setShowModal(true);
  };

  const openEditModal = (technique) => {
    setEditingTechnique(technique);
    setFormData({
      name: technique.name,
      slug: technique.slug,
      description: technique.description || '',
      icon: technique.icon || 'brain',
      system_prompt_addition: technique.system_prompt_addition || '',
      sort_order: technique.sort_order,
      is_active: technique.is_active,
      levels: technique.levels || ['L1', 'L2']
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTechnique) {
        await techniqueApi.updateTechnique(editingTechnique.id, formData);
        toast.success('Technique updated');
      } else {
        await techniqueApi.createTechnique(formData);
        toast.success('Technique created');
      }
      setShowModal(false);
      loadTechniques();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save technique');
    }
  };

  const handleDelete = async (technique) => {
    if (!confirm(`Deactivate "${technique.name}"? It will be hidden from students.`)) return;

    try {
      await techniqueApi.deleteTechnique(technique.id);
      toast.success('Technique deactivated');
      loadTechniques();
    } catch (error) {
      toast.error('Failed to deactivate technique');
    }
  };

  const handleToggleActive = async (technique) => {
    try {
      await techniqueApi.updateTechnique(technique.id, { is_active: !technique.is_active });
      toast.success(technique.is_active ? 'Technique hidden' : 'Technique activated');
      loadTechniques();
    } catch (error) {
      toast.error('Failed to update technique');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Techniques</h1>
          <p className="text-gray-600">Create and manage memory technique chat areas</p>
        </div>
        {isAdmin && (
          <button
            onClick={openCreateModal}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Technique
          </button>
        )}
      </div>

      {/* Techniques List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Order</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Technique</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Slug</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Levels</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
              {isAdmin && <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {techniques.map((technique) => (
              <tr key={technique.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center text-gray-400">
                    <GripVertical className="w-4 h-4 mr-1" />
                    {technique.sort_order}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-sm">{technique.icon?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{technique.name}</p>
                      <p className="text-sm text-gray-500 truncate max-w-xs">
                        {technique.description}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {technique.slug}
                  </code>
                </td>
                <td className="px-4 py-3">
                  <div className="flex space-x-1">
                    {(technique.levels || ['L1', 'L2']).map(level => (
                      <span key={level} className={`text-xs px-2 py-0.5 rounded-full ${
                        level === 'L1' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {level}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {isAdmin ? (
                    <button
                      onClick={() => handleToggleActive(technique)}
                      className={`px-2 py-1 text-xs rounded-full font-medium ${
                        technique.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {technique.is_active ? 'Active' : 'Hidden'}
                    </button>
                  ) : (
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      technique.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {technique.is_active ? 'Active' : 'Hidden'}
                    </span>
                  )}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => setTestTechnique(technique)}
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Test AI chat"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(technique)}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(technique)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {techniques.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No techniques yet.{isAdmin && ' Click "Add Technique" to create one.'}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingTechnique ? 'Edit Technique' : 'Create Technique'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        name: e.target.value,
                        slug: editingTechnique ? formData.slug : generateSlug(e.target.value)
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Memory Palace"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug *
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="memory-palace"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Learn the ancient method of loci technique"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon
                  </label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {ICONS.map((icon) => (
                      <option key={icon.value} value={icon.value}>
                        {icon.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Available Levels
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select which levels can access this technique.
                </p>
                <div className="flex space-x-4">
                  {['L1', 'L2'].map(level => (
                    <label key={level} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.levels.includes(level)}
                        onChange={(e) => {
                          const newLevels = e.target.checked
                            ? [...formData.levels, level]
                            : formData.levels.filter(l => l !== level);
                          if (newLevels.length > 0) {
                            setFormData({ ...formData, levels: newLevels });
                          }
                        }}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">
                        {level} {level === 'L1' ? '(Trial)' : '(Paid)'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AI Prompt Addition
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  This text is appended to the AI's base memory coach prompt when students chat about this technique.
                </p>
                <textarea
                  value={formData.system_prompt_addition}
                  onChange={(e) => setFormData({ ...formData, system_prompt_addition: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 h-32"
                  placeholder="Focus on teaching the Memory Palace (Method of Loci) technique. Guide the student through creating vivid mental locations and placing memorable images."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Active (visible to students)
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingTechnique ? 'Save Changes' : 'Create Technique'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Test Modal */}
      {testTechnique && (
        <TestTechniqueModal
          technique={testTechnique}
          onClose={() => setTestTechnique(null)}
        />
      )}
    </div>
  );
}

function TestTechniqueModal({ technique, onClose }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isSmallEnough = file.size <= 10 * 1024 * 1024;
      if (!isValid) toast.error(`${file.name} is not a supported file type`);
      if (!isSmallEnough) toast.error(`${file.name} is too large (max 10MB)`);
      return isValid && isSmallEnough;
    });
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 5));
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    const text = message.trim();
    if ((!text && selectedFiles.length === 0) || loading) return;

    const filesToUpload = [...selectedFiles];
    const imagePreviews = filesToUpload
      .filter(f => f.type.startsWith('image/'))
      .map(f => ({ name: f.name, url: URL.createObjectURL(f) }));
    const pdfNames = filesToUpload
      .filter(f => !f.type.startsWith('image/'))
      .map(f => f.name);

    setMessages(prev => [...prev, { role: 'user', content: text || '', images: imagePreviews, pdfNames }]);
    setMessage('');
    setSelectedFiles([]);
    setLoading(true);

    try {
      let fileIds = [];
      if (filesToUpload.length > 0) {
        setIsUploading(true);
        const uploadRes = await uploadApi.uploadFiles(filesToUpload);
        fileIds = uploadRes.data.files.map(f => f.id);
        setIsUploading(false);
      }

      const res = await adminApi.testTechnique(technique.id, text, fileIds);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.message,
        contextCount: res.data.contextCount
      }]);
    } catch (error) {
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error: Failed to get response from AI.'
      }]);
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl h-[80vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">Test: {technique.name}</h2>
            <p className="text-xs text-gray-500">Admin test — no quota deducted</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-gray-400 mt-8">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Send a message to test the AI for <strong>{technique.name}</strong></p>
              <p className="text-xs mt-1">Uses technique prompt + knowledge base context</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-xl px-4 py-2 ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}>
                {msg.images && msg.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {msg.images.map((img, j) => (
                      <img key={j} src={img.url} alt={img.name} className="max-w-[200px] max-h-[150px] rounded-lg object-cover" />
                    ))}
                  </div>
                )}
                {msg.pdfNames && msg.pdfNames.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {msg.pdfNames.map((name, j) => (
                      <span key={j} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${msg.role === 'user' ? 'bg-primary-700' : 'bg-gray-200'}`}>
                        <FileText className="w-3 h-3" /> {name}
                      </span>
                    ))}
                  </div>
                )}
                {msg.content && <div className="whitespace-pre-wrap text-sm">{msg.content}</div>}
                {msg.contextCount != null && (
                  <div className="text-xs mt-1 opacity-60">
                    {msg.contextCount} KB {msg.contextCount === 1 ? 'entry' : 'entries'} used
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-xl px-4 py-3">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t">
          {/* File preview */}
          {selectedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedFiles.map((file, i) => (
                <div key={i} className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 text-xs">
                  {file.type.startsWith('image/') ? <Image className="w-3 h-3 text-blue-500" /> : <FileText className="w-3 h-3 text-red-500" />}
                  <span className="max-w-[120px] truncate">{file.name}</span>
                  <button onClick={() => removeFile(i)} className="ml-1 text-gray-400 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {isUploading && <p className="text-xs text-gray-400 mb-2">Uploading files...</p>}
          <div className="flex items-end gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,.pdf"
              multiple
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors shrink-0"
              title="Attach files (images or PDFs)"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a test message..."
              rows={1}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
            <button
              onClick={handleSend}
              disabled={(!message.trim() && selectedFiles.length === 0) || loading}
              className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageTechniques;
